import React from "react";

import { useTranslation } from "react-i18next";

import { Driver, DriverResponse } from "@lib/types/driver";
import ViewDialog from "@components/Dialog";
import InformationView from "@components/InformationView";
import { Divider, Box } from "@mui/material";
import CustomTabs, {
  TabContent,
  TabGroup,
  TabName,
} from "@components/CustomTab";
import { parseValue } from "@lib/utils/string";
import { useQuery } from "@tanstack/react-query";
import { BindingsList } from "@lib/types/binding";
import { getBindings } from "@lib/api/queries/bindings";
import BindingCard from "@components/BindingsCard";

interface Props {
  updateDriverDialogOpen: boolean;
  setUpdateDriverDialogOpen: (open: boolean) => void;
  driverData?: Driver;
}

const ViewDriver: React.FC<Props> = ({
  updateDriverDialogOpen,
  setUpdateDriverDialogOpen,
  driverData,
}) => {
  const { t } = useTranslation();

  const userInfo = [
    { label: "Name", value: parseValue(driverData?.name), columnSize: 2 },
    {
      label: "Parameter Number",
      value: parseValue(driverData?.parameter_number),
      columnSize: 2,
    },
    { label: "Code", value: parseValue(driverData?.code), columnSize: 2 },
    { label: "Phone", value: parseValue(driverData?.phone), columnSize: 2 },
    {
      label: "Mobile Key",
      value: parseValue(driverData?.mobile_key),
      columnSize: 2,
    },
    {
      label: "Exclusive",
      value: driverData?.exclusive ? "Yes" : "No",
      columnSize: 2,
    },
    {
      label: "Description",
      value: parseValue(driverData?.description),
      columnSize: 4,
    },
  ];

  const customFields = driverData?.custom_fields.map((field) => ({
    label: field.key,
    value: parseValue(field.value),
  }));

  const {
    data: bindings,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery<BindingsList>({
    queryKey: ["bindings", driverData?.id],
    queryFn: () => getBindings(driverData?.id),
    refetchOnWindowFocus: false,
    enabled: !!driverData?.id,
  });

  return (
    <ViewDialog
      title={t("user.drivers.view")}
      isOpen={updateDriverDialogOpen}
      handleClose={() => {
        setUpdateDriverDialogOpen(false);
      }}
    >
      <InformationView title="User Information" data={userInfo} columns={4} />

      {customFields && customFields.length > 0 && (
        <>
          <Divider />
          <InformationView title="Custom Fields" data={customFields} />
        </>
      )}

      {!isFetching && (
        <Box flex={1} sx={{ borderTop: 1, borderColor: "divider" }}>
          <CustomTabs>
            <TabGroup>
              <TabName>{t("user.drivers.activeBindings")}</TabName>
              <TabContent>
                <Box
                  flex={1}
                  flexDirection={"column"}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    padding: 2,
                  }}
                >
                  {bindings?.bindings && bindings.bindings.length > 0 ? (
                    bindings.bindings
                      .filter((binding) => binding.status === "active")
                      .map((binding) => <BindingCard binding={binding} />)
                  ) : (
                    <Box>{t("user.drivers.noActiveBindings")}</Box>
                  )}
                </Box>
              </TabContent>
            </TabGroup>
            <TabGroup>
              <TabName>{t("user.drivers.inactiveBindings")}</TabName>
              <TabContent>
                <Box
                  flex={1}
                  flexDirection={"column"}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    padding: 2,
                  }}
                >
                  {bindings?.bindings && bindings.bindings.length > 0 ? (
                    bindings.bindings
                      .filter((binding) => binding.status === "inactive")
                      .map((binding) => <BindingCard binding={binding} />)
                  ) : (
                    <Box>{t("user.drivers.noInactiveBindings")}</Box>
                  )}
                </Box>
              </TabContent>
            </TabGroup>
          </CustomTabs>
        </Box>
      )}
    </ViewDialog>
  );
};

export default ViewDriver;
